import {DotsHorizontalIcon} from '@heroicons/react/outline';
import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';
import {hasAdminPermission} from '@afriex/core/utils';

import {Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import DeactivateReasonModal from '@/components/Modals/ActivationModal';
import UpdateRoleModal from '@/components/Modals/UpdateRoleModal';
import {doNothing} from '@/constants/formatters';
import useCloseOnOutsideClick from '@/hooks/useCloseOnOutsideClick';
import useUserDetails from '@/screens/User/Details/useUserDetails';
import type {AfriexAdmin} from '@/types';
import {AfriexRoles} from '@/types/AfriexRoles';
import {formatDate} from '@/utils/date';

const AdminTable = ({items}: {items: AfriexAdmin[]}) => {
  return (
    <Table>
      <Thead>
        <Tr enableHover={false}>
          <Th className="pl-6">Personnel Name</Th>
          <Th className="px-4">Email</Th>
          <Th className="px-4">Role</Th>
          <Th className="px-4">Last Login</Th>
          <Th className="px-4">Actions</Th>
        </Tr>
      </Thead>

      <TBody>
        {items.map((item, index) => (
          <AdminTableRow item={item} key={item.userId} index={index} />
        ))}
      </TBody>
    </Table>
  );
};

const AdminTableRow = ({item, index}: {item: AfriexAdmin; index: number}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [openDeactivateModal, setOpenDeactivateModal] = useState(false);

  const loggedInRole = localStorage.getItem('admin-role');
  const {user} = useUserDetails(item.userId);

  const toggleRoleModal = () => setIsRoleModalOpen(!isRoleModalOpen);

  // The menu holds Update Role and Deactivate, so it opens for a role that
  // may do either.
  const hasOffboardingAccess =
    hasAdminPermission(
      loggedInRole ?? undefined,
      AfriexAdminPermission.UserRoleUpdate,
    ) ||
    hasAdminPermission(
      loggedInRole ?? undefined,
      AfriexAdminPermission.UserDeactivate,
    );

  const isEngineeringManager = item.role === AfriexRoles.ENGINEERING_MANAGER;
  const openActions = () => setShowOptions((prev) => !prev);
  const closeActions = () => setShowOptions(false);
  const containerRef = useCloseOnOutsideClick<HTMLDivElement>(closeActions);
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';
  return (
    <>
      <Tr className={zebraRow}>
        <Td className={`pl-6 py-4 text-left font-medium`}>{item.fullName}</Td>
        <Td className={`px-4 text-left`}>{item.email}</Td>
        <Td className={`px-4 text-left`}>{item.role || '-'}</Td>
        <Td className={`px-4 text-left`}>{formatDate(item.updatedAt || '')}</Td>
        <Td className={`px-4 text-left relative`}>
          <div
            ref={containerRef}
            onClick={
              hasOffboardingAccess && !isEngineeringManager
                ? openActions
                : doNothing
            }
            className="relative w-5 h-5 flex justify-center items-center text-gray-700 dark:text-gray-200 hover:border rounded-lg hover:border-gray-300 hover:bg-cyan-50 cursor-pointer"
          >
            <DotsHorizontalIcon className="text-gray-700 dark:text-gray-200 w-4 h-4" />

            {showOptions && !user.isDeactivated && (
              <div className="absolute flex items-end flex-col top-4 right-0 bg-white dark:bg-dark-inputBg border dark:border-dark-borderHeavy py-3 pr-4 gap-3 z-10 rounded w-36">
                <PermissionsProvider
                  permission={AfriexAdminPermission.UserRoleUpdate}
                >
                  <Button
                    variant="unStyled"
                    className="dark:text-gray-200 hover:text-cyan-700 dark:hover:text-cyan-700"
                    onClick={toggleRoleModal}
                  >
                    Update Role
                  </Button>
                </PermissionsProvider>

                <PermissionsProvider
                  permission={AfriexAdminPermission.UserDeactivate}
                >
                  <Button
                    variant="unStyled"
                    className="hover:text-red-700 text-red-500"
                    onClick={() => setOpenDeactivateModal(true)}
                  >
                    Deactivate
                  </Button>
                </PermissionsProvider>
              </div>
            )}
          </div>
        </Td>
      </Tr>

      <DeactivateReasonModal
        isOpen={openDeactivateModal}
        onClose={() => setOpenDeactivateModal(false)}
        user={user}
        type="admin"
      />

      <UpdateRoleModal
        isOpen={isRoleModalOpen}
        onClose={() => setIsRoleModalOpen(false)}
        user={user}
        defaultRole={item.role}
      />
    </>
  );
};

export default AdminTable;
